// project/src/components/VoiceCloneCreator.tsx
//
// Sub-vista de creación de voz clonada. Se monta dentro del voice picker
// (NO es un modal). Maneja:
//   - Input de nombre
//   - Grabar in-browser o subir archivo (30s-3min)
//   - Checkbox de consentimiento legal
//   - Submit que llama a createClonedVoice y devuelve la voz creada al
//     parent para que la agregue al estado
//
// Reusa la mecánica de MediaRecorder de DialogueAudioInput.tsx pero
// con duración mínima 30s (vs 60s del diálogo).

import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  Mic,
  Square,
  Play,
  Pause,
  Upload,
  Loader2,
  X,
  AlertCircle,
} from 'lucide-react';
import {
  ClonedVoice,
  CloneVoiceError,
  createClonedVoice,
} from '../lib/clonedVoicesApi';
import {
  ALLOWED_SAMPLE_EXTENSIONS,
  MAX_SAMPLE_DURATION_SEC,
  MIN_SAMPLE_DURATION_SEC,
} from '../constants/voiceCloning';

export interface VoiceCloneCreatorProps {
  userId: string;
  companyId: string;
  onCreated: (voice: ClonedVoice) => void;
  onBack: () => void;
}

type AudioState =
  | { kind: 'idle' }
  | { kind: 'recording'; startedAt: number; durationSec: number }
  | { kind: 'ready'; blob: Blob; fileName: string; durationSec: number; previewUrl: string };

type SubmitState = 'idle' | 'submitting' | 'error';

function formatDuration(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const s = Math.floor(seconds);
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}:${ss.toString().padStart(2, '0')}`;
}

async function probeAudioDuration(file: File | Blob): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      const d = audio.duration;
      URL.revokeObjectURL(url);
      resolve(d);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('No se pudo leer la duración del audio'));
    };
    audio.src = url;
  });
}

const VoiceCloneCreator: React.FC<VoiceCloneCreatorProps> = ({
  userId,
  companyId,
  onCreated,
  onBack,
}) => {
  const [nombre, setNombre] = useState('');
  const [audioState, setAudioState] = useState<AudioState>({ kind: 'idle' });
  const [consent, setConsent] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const recordingTimerRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const playerRef = useRef<HTMLAudioElement | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    // En React 18 Strict Mode (dev), este effect corre dos veces: el primer
    // run setea `mountedRef.current = false` en su cleanup, y el segundo run
    // necesita re-setearlo a true para que `onstop` no retorne temprano.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanupRecording();
      if (audioState.kind === 'ready') {
        URL.revokeObjectURL(audioState.previewUrl);
      }
      if (playerRef.current) {
        playerRef.current.onended = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanupRecording = () => {
    if (recordingTimerRef.current != null) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    try {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        mediaRecorderRef.current.stop();
      }
    } catch {}
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  };

  const startRecording = async () => {
    try {
      setErrorMessage(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      // mimeType con fallback — algunos browsers (Safari) no soportan webm/opus.
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/mp4';
      }
      const rec = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = rec;
      chunksRef.current = [];

      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      const startedAt = Date.now();
      // Patrón de DialogueAudioInput: todo el cleanup (tracks, timer) sucede
      // DENTRO de onstop, no en stopRecording. Eso asegura que onstop dispare
      // antes de cortar el stream source.
      rec.onstop = () => {
        console.log('[VoiceCloneCreator] onstop fired, chunks:', chunksRef.current.length);
        const durationSec = (Date.now() - startedAt) / 1000;
        const blob = new Blob(chunksRef.current, { type: mimeType });
        mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
        if (recordingTimerRef.current != null) {
          window.clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        if (!mountedRef.current) return;
        if (blob.size === 0) {
          setErrorMessage('La grabación quedó vacía, probá de nuevo.');
          setAudioState({ kind: 'idle' });
          return;
        }
        const previewUrl = URL.createObjectURL(blob);
        const ext = mimeType.includes('mp4') ? 'm4a' : 'webm';
        console.log('[VoiceCloneCreator] setting state to ready, duration:', durationSec, 'size:', blob.size);
        setAudioState({
          kind: 'ready',
          blob,
          fileName: `grabacion.${ext}`,
          durationSec,
          previewUrl,
        });
      };

      rec.start();
      setAudioState({ kind: 'recording', startedAt, durationSec: 0 });
      recordingTimerRef.current = window.setInterval(() => {
        const elapsed = (Date.now() - startedAt) / 1000;
        setAudioState((prev) =>
          prev.kind === 'recording'
            ? { ...prev, durationSec: elapsed }
            : prev,
        );
        if (elapsed >= MAX_SAMPLE_DURATION_SEC) {
          stopRecording();
        }
      }, 200);
    } catch (e) {
      setErrorMessage(
        'No pudimos acceder al micrófono. Revisá los permisos.',
      );
      cleanupRecording();
      setAudioState({ kind: 'idle' });
    }
  };

  const stopRecording = () => {
    console.log('[VoiceCloneCreator] stopRecording called, state:', mediaRecorderRef.current?.state);
    try {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === 'recording'
      ) {
        mediaRecorderRef.current.stop();
      }
    } catch (e) {
      console.error('[VoiceCloneCreator] stopRecording error:', e);
    }
  };

  const onFilePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setErrorMessage(null);
    // Limpiar audio previo si había uno listo
    if (audioState.kind === 'ready') {
      URL.revokeObjectURL(audioState.previewUrl);
    }
    if (playerRef.current) {
      playerRef.current.pause();
      playerRef.current = null;
    }
    setIsPlaying(false);
    const ext = file.name.match(/\.([a-zA-Z0-9]+)$/)?.[1]?.toLowerCase();
    if (!ext || !ALLOWED_SAMPLE_EXTENSIONS.includes(`.${ext}`)) {
      setErrorMessage('Formato no soportado. Usá MP3, M4A, WAV, OGG o WebM.');
      return;
    }
    const durationSec = await probeAudioDuration(file).catch(() => 0);
    const previewUrl = URL.createObjectURL(file);
    setAudioState({
      kind: 'ready',
      blob: file,
      fileName: file.name,
      durationSec,
      previewUrl,
    });
  };

  const togglePreview = () => {
    if (audioState.kind !== 'ready') return;
    if (isPlaying) {
      playerRef.current?.pause();
      setIsPlaying(false);
      return;
    }
    if (!playerRef.current) {
      const a = new Audio(audioState.previewUrl);
      playerRef.current = a;
      a.onended = () => setIsPlaying(false);
    }
    playerRef.current.play().catch(() => setIsPlaying(false));
    setIsPlaying(true);
  };

  const resetAudio = () => {
    if (audioState.kind === 'ready') {
      URL.revokeObjectURL(audioState.previewUrl);
    }
    playerRef.current?.pause();
    if (playerRef.current) {
      playerRef.current.onended = null;
    }
    playerRef.current = null;
    setIsPlaying(false);
    setAudioState({ kind: 'idle' });
  };

  const canSubmit =
    audioState.kind === 'ready' &&
    audioState.durationSec >= MIN_SAMPLE_DURATION_SEC &&
    audioState.durationSec <= MAX_SAMPLE_DURATION_SEC &&
    nombre.trim().length > 0 &&
    consent &&
    submitState !== 'submitting';

  const handleSubmit = async () => {
    if (audioState.kind !== 'ready') return;
    setSubmitState('submitting');
    setErrorMessage(null);
    try {
      const created = await createClonedVoice({
        userId,
        companyId,
        nombre,
        audioBlob: audioState.blob,
        audioFileName: audioState.fileName,
        durationSec: audioState.durationSec,
        consentimientoAceptado: consent,
      });
      setSubmitState('idle');
      onCreated(created);
    } catch (e) {
      if (e instanceof CloneVoiceError) {
        setErrorMessage(e.message);
      } else {
        setErrorMessage('Hubo un problema técnico. Intentá de nuevo.');
      }
      setSubmitState('error');
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 h-12">
        <button
          type="button"
          onClick={onBack}
          className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
          aria-label="Volver"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h3 className="text-[15px] font-semibold text-brand-black/90">
          Crear voz clonada
        </h3>
      </div>
      <div className="h-px bg-black/[0.08]" />

      <div className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-4">
        {/* Nombre */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="voice-clone-nombre" className="text-[13px] font-medium text-brand-black/70">
            Nombre de la voz
          </label>
          <input
            id="voice-clone-nombre"
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Ej: Mi voz personal"
            maxLength={50}
            className="px-3 py-2 rounded-md border border-neutral-200 bg-white text-[14px] text-brand-black/90 focus:outline-none focus:border-brand-primary"
          />
        </div>

        {/* Sample de audio */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-brand-black/70">
            Sample de audio (30s - 3min)
          </label>

          {audioState.kind === 'idle' && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={startRecording}
                className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 px-3 rounded-md bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/85 text-[14px] font-medium transition-colors"
              >
                <Mic className="w-4 h-4" />
                Grabar
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 px-3 rounded-md bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/85 text-[14px] font-medium transition-colors"
              >
                <Upload className="w-4 h-4" />
                Subir
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_SAMPLE_EXTENSIONS.join(',')}
                onChange={onFilePicked}
                className="hidden"
              />
            </div>
          )}

          {audioState.kind === 'recording' && (
            <div className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-md bg-red-50 border border-red-200">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-[14px] font-medium text-brand-black/85">
                  Grabando… {formatDuration(audioState.durationSec)}
                </span>
              </div>
              <button
                type="button"
                onClick={stopRecording}
                className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-md bg-red-500 hover:bg-red-600 text-white text-[13px] font-medium transition-colors"
              >
                <Square className="w-3.5 h-3.5" />
                Parar
              </button>
            </div>
          )}

          {audioState.kind === 'ready' && (
            <div className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-md bg-neutral-50 border border-neutral-200">
              <button
                type="button"
                onClick={togglePreview}
                aria-label={isPlaying ? 'Pausar' : 'Reproducir'}
                className="w-9 h-9 rounded-full flex items-center justify-center bg-brand-primary text-white shrink-0"
              >
                {isPlaying ? (
                  <Pause className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4 ml-0.5" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-medium text-brand-black/90 truncate">
                  Audio listo
                </p>
                <p className="text-[12px] text-brand-black/55">
                  Duración: {formatDuration(audioState.durationSec)}
                </p>
              </div>
              <button
                type="button"
                onClick={resetAudio}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                aria-label="Quitar audio"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {audioState.kind === 'ready' &&
            audioState.durationSec < MIN_SAMPLE_DURATION_SEC && (
              <p className="text-[12px] text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                El audio debe durar al menos 30 segundos.
              </p>
            )}
          {audioState.kind === 'ready' &&
            audioState.durationSec > MAX_SAMPLE_DURATION_SEC && (
              <p className="text-[12px] text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                El audio no puede superar los 3 minutos.
              </p>
            )}
        </div>

        {/* Consentimiento */}
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-neutral-300 text-brand-primary focus:ring-brand-primary"
          />
          <span className="text-[13px] text-brand-black/70 leading-relaxed">
            Confirmo que tengo derechos sobre esta voz y acepto los términos de
            uso de ElevenLabs para clonación.
          </span>
        </label>

        {/* Error message */}
        {errorMessage && (
          <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200">
            <p className="text-[13px] text-red-700 flex items-start gap-1.5">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </p>
          </div>
        )}
      </div>

      {/* Footer con botón submit */}
      <div className="border-t border-black/[0.08] px-3 py-3">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-[14.5px] font-semibold transition-colors ${
            canSubmit
              ? 'bg-brand-primary text-white hover:bg-brand-primary/90'
              : 'bg-neutral-200 text-neutral-500 cursor-not-allowed'
          }`}
        >
          {submitState === 'submitting' && (
            <Loader2 className="w-4 h-4 animate-spin" />
          )}
          {submitState === 'submitting' ? 'Creando voz…' : 'Crear voz clonada'}
        </button>
      </div>
    </div>
  );
};

export default VoiceCloneCreator;
